import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Chip,
    Button,
    CircularProgress,
} from '@mui/material';
import {
    selectRouteType,
    selectStartPoint,
    selectEndPoint,
    selectViaPoints,
    selectRoutePath,
    selectRouteDistance,
    selectRouteTime,
    setComputeRoute,
    generateRouteSegments,
    setWizardStepCompleted,
    setIsMinimized,
    setCurrentWizardStep,
    addViaPoint,
    clearViaPoints,
    setEndPoint
} from '../../../features/tripcenter/googleRouteMakerSlice';
import { setRouteMaker } from '../../../features/tripcenter/routeSlice';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import PropTypes from 'prop-types';

// Icons
import RouteIcon from '@mui/icons-material/Route';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StraightenIcon from '@mui/icons-material/Straighten';
import ReplayIcon from '@mui/icons-material/Replay';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlaceIcon from '@mui/icons-material/Place';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import { blue, orange, purple, red } from '@mui/material/colors';

// Route type definitions
const routeTypes = [
    {
        id: 'oneWayDirect',
        title: 'One Way Direct',
    },
    {
        id: 'roundTrip',
        title: 'Round Trip',
    },
    {
        id: 'oneWayWithStops',
        title: 'One Way with Stops',
    },
    {
        id: 'roundTripWithStops',
        title: 'Round Trip with Stops',
    }
];

const CompactRouteBuilder = () => {
    const dispatch = useDispatch();
    const layout = useSelector(liveTrackinglayoutSelector);
    const routeType = useSelector(selectRouteType);
    const startPoint = useSelector(selectStartPoint);
    const endPoint = useSelector(selectEndPoint);
    const viaPoints = useSelector(selectViaPoints);
    const routePath = useSelector(selectRoutePath);
    const routeDistance = useSelector(selectRouteDistance);
    const routeTime = useSelector(selectRouteTime);
    const [isCalculating, setIsCalculating] = React.useState(false);
    const [error, setError] = React.useState(null);

    const formattedDistance = (routeDistance / 1000).toFixed(1);
    const formattedTime = formatTime(routeTime);
    const routeBuilt = routePath && routePath.length > 0;
    function formatTime(seconds) {
        if (!seconds) return '0 min';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;
    }

    const canCalculateRoute = () => startPoint && endPoint;

    /* const handleCalculateRoute = () => {
        setIsCalculating(true);
        setError(null);
        // For round trips, modify the route calculation
        if (routeType === 'roundTrip' || routeType === 'roundTripWithStops') {
            // Clear existing via points first
            dispatch(clearViaPoints());

            // Add the end point as a via point
            if (endPoint) {
                // Make sure we're sending the data in the correct format
                dispatch(addViaPoint({
                    lat: endPoint.lat,
                    lng: endPoint.lng,
                    address: endPoint.address,
                    locationName: endPoint.locationName,
                    locationType: endPoint.locationType,
                    zoneCoordinates: endPoint.zoneCoordinates,
                    triggerRadius: endPoint.triggerRadius,
                    maxDetentionTime: endPoint.maxDetentionTime
                }));
            }

            // Set the end point to be the same as start point for round trip
            dispatch(setEndPoint({
                ...startPoint,
                locationName: `${startPoint.locationName} (Return)`
            }));
        } else {
            //console.log("RouteBuilder: Calculating Simple route...", routeType);
        }

        // Trigger route calculation
        dispatch(setComputeRoute(true));

        // This would normally be handled by the GoogleRouteMaker component
        // which would update the route data in Redux
        // For now, we'll simulate completion after a short delay
        setTimeout(() => {
            setIsCalculating(false);

            // If the route was calculated successfully, update step completion
            if (routePath && routePath.length > 0) {
                dispatch(setWizardStepCompleted({ step: 'route', completed: true }));

                // Generate route segments
                dispatch(generateRouteSegments());
            } else {
                setError('Route calculation failed. Please try again.');
            }
        }, 2000);
    }; */
    // Calculate route
    const handleCalculateRoute = () => {
        setIsCalculating(true);
        setError(null);
        console.log("RouteBuilder: Calculating route...", routeType);
        // For round trips, modify the route calculation
        if (routeType === 'roundTrip' || routeType === 'roundTripWithStops') {
            // Clear existing via points first
            if (routeType === 'roundTrip') {
                console.log("RouteBuilder: Clearing via points for round trip", routeType);
                dispatch(clearViaPoints());
            }

            // Check if endPoint exists and is not the same as the startPoint
            if (endPoint && (endPoint.lat !== startPoint.lat || endPoint.lng !== startPoint.lng)) {
                // Make sure we're sending the data in the correct format
                dispatch(addViaPoint({
                    lat: endPoint.lat,
                    lng: endPoint.lng,
                    address: endPoint.address,
                    locationName: endPoint.locationName,
                    locationType: endPoint.locationType,
                    zoneCoordinates: endPoint.zoneCoordinates,
                    triggerRadius: endPoint.triggerRadius,
                    maxDetentionTime: endPoint.maxDetentionTime
                }));
            }

            // Set the end point to be the same as start point for round trip
            dispatch(setEndPoint({
                ...startPoint,
                locationName: `${startPoint.locationName} (Return)`
            }));
        }

        // Trigger route calculation
        dispatch(setComputeRoute(true));

        // This would normally be handled by the GoogleRouteMaker component
        // which would update the route data in Redux
        // For now, we'll simulate completion after a short delay
        setTimeout(() => {
            setIsCalculating(false);

            // If the route was calculated successfully, update step completion
            if (routePath && routePath.length > 0) {
                dispatch(setWizardStepCompleted({ step: 'route', completed: true }));

                // Generate route segments
                dispatch(generateRouteSegments());
            } else {
                setError('Route calculation failed. Please try again.');
            }
        }, 2000);
    };

    const handleMaximize = () => {
        //console.log("CompactRouteBuilder: Maximizing...");
        dispatch(setIsMinimized(false));
    };

    const handleClose = () => {
        dispatch(setRouteMaker(null));
    };

    const handleConfirmRoute = () => {
        //console.log("Starting route confirmation...");

        // First mark the step as completed
        dispatch(setWizardStepCompleted({ step: 'route', completed: true }));

        // Then generate segments
        if (routePath && routePath.length > 0) {
            dispatch(generateRouteSegments());
        }

        // Finally update step and minimize state
        dispatch(setCurrentWizardStep('segments'));
        dispatch(setIsMinimized(false));
    };

    return (
        <Paper
            elevation={3}
            sx={{
                ...layout.lowersidebar,
                maxWidth: '400px',
                borderRadius: '8px',
            }}
        >
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8
            }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Route Builder
                </Typography>
                <Box>
                    <IconButton
                        onClick={handleMaximize}
                        color="inherit"
                        size="small"
                        sx={{ mr: 1 }}
                    >
                        <OpenInFullIcon />
                    </IconButton>
                    <IconButton
                        onClick={handleClose}
                        color="inherit"
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ p: 2, gap: 2 }}>
                {/* Route Type Display */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        mb: 2
                    }}
                >
                        <RouteIcon
                        fontSize="small"
                            sx={{
                                mr: 1.5,
                                color: 'primary.main'
                            }}
                    />
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 600,
                            color: 'primary.dark'
                        }}
                    >
                        {routeTypes.find(rt => rt.id === routeType)?.title || ''}
                    </Typography>
                </Box>
                {/* Route Summary with improved layout */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        mb: 2,
                    }}
                >
                    {/* Route Path Visualization */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                            p: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}
                    >
                        {/* Start Location */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: blue[50],
                                color: blue[700],
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: blue[100],
                                flexShrink: 0
                            }}
                        >
                            <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: blue[700] }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: {
                                        xs: '150px',
                                        sm: '200px',
                                        md: '250px'
                                    }
                                }}
                            >
                                {startPoint?.locationName || 'Start'}
                            </Typography>
                        </Box>

                        {/* Via Points */}
                        {viaPoints.length > 0 && viaPoints.map((via, index) => (
                            <React.Fragment key={index}>
                                <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary', mx: 0.5, flexShrink: 0 }} />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: purple[50],
                                        color: purple[700],
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: purple[100],
                                        flexShrink: 0
                                    }}
                                >
                                    <PlaceIcon fontSize="small" sx={{ mr: 0.5, color: purple[700] }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: {
                                                xs: '120px',
                                                sm: '150px',
                                                md: '200px'
                                            }
                                        }}
                                    >
                                        {via.locationName || `Stop ${index + 1}`}
                                    </Typography>
                                </Box>
                            </React.Fragment>
                        ))}

                        {/* Arrow to End Location */}
                        <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary', mx: 0.5, flexShrink: 0 }} />

                        {/* End Location */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: red[50],
                                color: red[800],
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: red[100],
                                flexShrink: 0
                            }}
                        >
                            <FmdGoodIcon fontSize="small" sx={{ mr: 0.5, color: red[800] }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: {
                                        xs: '150px',
                                        sm: '200px',
                                        md: '250px'
                                    }
                                }}
                            >
                                {endPoint?.locationName || 'End'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Summary Line with Total Count */}
                    {/* <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, ml: 1 }}
                    >
                        {viaPoints.length > 0
                            ? `Route with ${viaPoints.length} intermediate stop${viaPoints.length > 1 ? 's' : ''}`
                            : 'Direct route with no stops'
                        }
                    </Typography> */}
                </Box>

                {/* Route Adjustment Tip */}
                {routeBuilt && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            mt: 1,
                            gap: 1,
                            mb: 2,
                            p: 1.5,
                            bgcolor: 'info.lighter',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'info.light',
                        }}
                    >
                        <DragIndicatorIcon
                            sx={{
                                color: 'info.main',
                                fontSize: '1.2rem',
                                mt: '2px'
                            }}
                        />
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'info.dark',
                                    fontWeight: 500,
                                    mb: 0.5
                                }}
                            >
                                Customize your route
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: 'info.dark' }}
                            >
                                You can drag any point on the route to adjust it. The route will automatically recalculate based on your changes.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 2,
                            '& .MuiAlert-message': {
                                fontSize: '0.875rem'
                            }
                        }}
                    >
                        {error}
                    </Alert>
                )} */}

                {/* Route Details and Actions */}
                {routeBuilt ? (
                    <>
                        {/* Distance and Duration */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 3,
                                mb: 2,
                                ml: 0,
                                p: 1.5,
                                backgroundColor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                '& .MuiSvgIcon-root': {
                                    fontSize: '1.2rem',
                                    color: 'primary.main'
                                },
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                    transition: 'background-color 0.2s'
                                }
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <StraightenIcon />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary'
                                    }}
                                >
                                    {formattedDistance} km
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AccessTimeIcon />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary'
                                    }}
                                >
                                    {formattedTime}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ReplayIcon />}
                                onClick={handleCalculateRoute}
                                size="small"
                            >
                                Rebuild Route
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmRoute}
                                size="small"
                            >
                                Confirm Route
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={isCalculating ? null : <RouteIcon />}
                        onClick={handleCalculateRoute}
                        disabled={!canCalculateRoute() || isCalculating}
                        size="small"
                        fullWidth
                    >
                        {isCalculating ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Calculating...
                                <CircularProgress size={16} color="inherit" />
                            </Box>
                        ) : (
                            'Build Route'
                        )}
                    </Button>
                )}
            </Box>
        </Paper>
    );
};

export default CompactRouteBuilder; 